import { constants } from '@yola/subscription-manager-js';
import { t } from '../../localization';

const { ecommercePlanFeatureNames: ecommerceFeatures } = constants;

const ecommerceFeatureConfiguration = {
  [ecommerceFeatures.PRODUCT_LIMIT]: {
    config(availablePlans) {
      return {
        id: ecommerceFeatures.PRODUCT_LIMIT,
        title: t('Product limit'),
        disabled: false,
        availabilities: availablePlans.map(({ id, config }) => ({
          id,
          contentType: 'text',
          text: config[ecommerceFeatures.PRODUCT_LIMIT].toLocaleString('en-US'),
          compact: `${t('Product limit')}: ${config[ecommerceFeatures.PRODUCT_LIMIT].toLocaleString(
            'en-US'
          )}`,
        })),
      };
    },
  },
  [ecommerceFeatures.CATEGORIES]: {
    config(availablePlans) {
      return {
        id: ecommerceFeatures.CATEGORIES,
        title: t('Categories'),
        disabled: false,
        availabilities: availablePlans.map(({ id, config }) => ({
          id,
          contentType: 'text',
          text: config[ecommerceFeatures.CATEGORIES].toLocaleString('en-US'),
          compact: `${t('Categories')}: ${config[ecommerceFeatures.CATEGORIES].toLocaleString(
            'en-US'
          )}`,
        })),
      };
    },
  },
  [ecommerceFeatures.E_GOODS_MAX_SIZE]: {
    config(availablePlans) {
      return {
        id: ecommerceFeatures.E_GOODS_MAX_SIZE,
        title: t('E-goods'),
        tooltip: t(
          'Sell products that can be delivered electronically such as music files, ebooks, videos and images, and more'
        ),
        disabled: false,
        availabilities: availablePlans.map(({ id, config }) => ({
          id,
          contentType: config[ecommerceFeatures.E_GOODS_MAX_SIZE] ? 'text' : 'icon-minus',
          text: t('{megabytes} MB', {
            megabytes: config[ecommerceFeatures.E_GOODS_MAX_SIZE].toLocaleString('en-US'),
          }),
          compact: config[ecommerceFeatures.E_GOODS_MAX_SIZE]
            ? `${t('E-goods')}: ${t('{megabytes} MB', {
                megabytes: config[ecommerceFeatures.E_GOODS_MAX_SIZE].toLocaleString('en-US'),
              })}`
            : null,
        })),
      };
    },
  },
  [ecommerceFeatures.GOOGLE_SALES]: {
    config(availablePlans) {
      return {
        id: ecommerceFeatures.GOOGLE_SALES,
        title: t('Google Shopping'),
        tooltip: t(
          'Generate XML products feed for Google Shopping. Useful for merchants who need more control on Google Shopping ads.'
        ),
        disabled: false,
        availabilities: availablePlans.map(({ id, config }) => ({
          id,
          contentType: config[ecommerceFeatures.GOOGLE_SALES] ? 'icon-check' : 'icon-minus',
          compact: config[ecommerceFeatures.GOOGLE_SALES] ? t('Google Shopping') : null,
        })),
      };
    },
  },
  [ecommerceFeatures.FACEBOOK_SALES]: {
    config(availablePlans) {
      return {
        id: ecommerceFeatures.FACEBOOK_SALES,
        title: t('Sell on Facebook'),
        tooltip: t(
          'Automatically sync your store to your Facebook Business Page and start reaching over 2 billion unique shoppers'
        ),
        disabled: false,
        availabilities: availablePlans.map(({ id, config }) => ({
          id,
          contentType: config[ecommerceFeatures.FACEBOOK_SALES] ? 'icon-check' : 'icon-minus',
          compact: config[ecommerceFeatures.FACEBOOK_SALES] ? t('Sell on Facebook') : null,
        })),
      };
    },
  },
  [ecommerceFeatures.INSTAGRAM_SALES]: {
    config(availablePlans) {
      return {
        id: ecommerceFeatures.INSTAGRAM_SALES,
        title: t('Sell on Instagram'),
        tooltip: t(
          'Add shoppable product tags to your Instagram posts and let users view and order products right from their Instagram account'
        ),
        disabled: false,
        availabilities: availablePlans.map(({ id, config }) => ({
          id,
          contentType: config[ecommerceFeatures.INSTAGRAM_SALES] ? 'icon-check' : 'icon-minus',
          compact: config[ecommerceFeatures.INSTAGRAM_SALES] ? t('Sell on Instagram') : null,
        })),
      };
    },
  },
  [ecommerceFeatures.AMAZON_SALES]: {
    config(availablePlans) {
      return {
        id: ecommerceFeatures.AMAZON_SALES,
        title: t('Sell on Amazon'),
        tooltip: t(
          'Automatically sync your products to Amazon and get more customers. Manage the listings from your store’s control panel and sync your inventory automatically to prevent overselling'
        ),
        disabled: false,
        availabilities: availablePlans.map(({ id, config }) => ({
          id,
          contentType: config[ecommerceFeatures.AMAZON_SALES] ? 'icon-check' : 'icon-minus',
          compact: config[ecommerceFeatures.AMAZON_SALES] ? t('Sell on Amazon') : null,
        })),
      };
    },
  },
  [ecommerceFeatures.EBAY_SALES]: {
    config(availablePlans) {
      return {
        id: ecommerceFeatures.EBAY_SALES,
        title: t('Sell on eBay'),
        tooltip: t(
          'Automatically sync your products to eBay and get more customers. Manage all incoming orders from eBay in your store’s control panel while your product inventory syncs automatically'
        ),
        disabled: false,
        availabilities: availablePlans.map(({ id, config }) => ({
          id,
          contentType: config[ecommerceFeatures.EBAY_SALES] ? 'icon-check' : 'icon-minus',
          compact: config[ecommerceFeatures.EBAY_SALES] ? t('Sell on eBay') : null,
        })),
      };
    },
  },
  [ecommerceFeatures.FACEBOOK_ADVERTISING]: {
    config(availablePlans) {
      return {
        id: ecommerceFeatures.FACEBOOK_ADVERTISING,
        title: t('Automated advertising on Facebook'),
        tooltip: t(
          'Automatically create Facebook ads for your products and run campaigns tailored for your customers'
        ),
        disabled: false,
        availabilities: availablePlans.map(({ id, config }) => ({
          id,
          contentType: config[ecommerceFeatures.FACEBOOK_ADVERTISING] ? 'icon-check' : 'icon-minus',
          compact: config[ecommerceFeatures.FACEBOOK_ADVERTISING]
            ? t('Automated advertising on Facebook')
            : null,
        })),
      };
    },
  },
  [ecommerceFeatures.GOOGLE_ADVERTISING]: {
    config(availablePlans) {
      return {
        id: ecommerceFeatures.GOOGLE_ADVERTISING,
        title: t('Automated advertising on Google'),
        tooltip: t(
          'Automatically create Google Shopping ads for your products and run campaigns tailored for your customers'
        ),
        disabled: false,
        availabilities: availablePlans.map(({ id, config }) => ({
          id,
          contentType: config[ecommerceFeatures.GOOGLE_ADVERTISING] ? 'icon-check' : 'icon-minus',
          compact: config[ecommerceFeatures.GOOGLE_ADVERTISING]
            ? t('Automated advertising on Google')
            : null,
        })),
      };
    },
  },
  [ecommerceFeatures.TIKTOK_ADVERTISING]: {
    config(availablePlans) {
      return {
        id: ecommerceFeatures.TIKTOK_ADVERTISING,
        title: t('Automated advertising on TikTok'),
        tooltip: t(
          'Sync your Online Store catalog with TikTok automatically and run campaigns tailored for your customers'
        ),
        disabled: false,
        availabilities: availablePlans.map(({ id, config }) => ({
          id,
          contentType: config[ecommerceFeatures.TIKTOK_ADVERTISING] ? 'icon-check' : 'icon-minus',
          compact: config[ecommerceFeatures.TIKTOK_ADVERTISING]
            ? t('Automated advertising on TikTok')
            : null,
        })),
      };
    },
  },
  [ecommerceFeatures.INVENTORY_TRACKING]: {
    config(availablePlans) {
      return {
        id: ecommerceFeatures.INVENTORY_TRACKING,
        title: t('Inventory tracking'),
        tooltip: t(
          'Automatically track stock levels and make out of stock products unavailable for purchase. Get notified when quantities are low so you know when to restock.'
        ),
        disabled: false,
        availabilities: availablePlans.map(({ id, config }) => ({
          id,
          contentType: config[ecommerceFeatures.INVENTORY_TRACKING] ? 'icon-check' : 'icon-minus',
          compact: config[ecommerceFeatures.INVENTORY_TRACKING] ? t('Inventory tracking') : null,
        })),
      };
    },
  },
  [ecommerceFeatures.ABANDONED_CART_RECOVERY]: {
    config(availablePlans) {
      return {
        id: ecommerceFeatures.ABANDONED_CART_RECOVERY,
        title: t('Automatic abandoned cart recovery'),
        tooltip: t(
          'Set up automatic emails to remind customers about items they left in their cart without purchasing - one of the best ways to convert sales'
        ),
        disabled: false,
        availabilities: availablePlans.map(({ id, config }) => ({
          id,
          contentType: config[ecommerceFeatures.ABANDONED_CART_RECOVERY]
            ? 'icon-check'
            : 'icon-minus',
          compact: config[ecommerceFeatures.ABANDONED_CART_RECOVERY]
            ? t('Automatic abandoned cart recovery')
            : null,
        })),
      };
    },
  },
  [ecommerceFeatures.MULTILINGUAL]: {
    config(availablePlans) {
      return {
        id: ecommerceFeatures.MULTILINGUAL,
        title: t('Multilingual Store'),
        tooltip: t(
          'Let customers shop in their preferred language by adding product descriptions, options, and categories in multiple languages'
        ),
        disabled: false,
        availabilities: availablePlans.map(({ id, config }) => ({
          id,
          contentType: config[ecommerceFeatures.MULTILINGUAL] ? 'icon-check' : 'icon-minus',
          compact: config[ecommerceFeatures.MULTILINGUAL] ? t('Multilingual Store') : null,
        })),
      };
    },
  },
  [ecommerceFeatures.TAX_CALCULATIONS]: {
    config(availablePlans) {
      return {
        id: ecommerceFeatures.TAX_CALCULATIONS,
        title: t('Automatic tax calculations'),
        tooltip: t(
          'Automatically calculate up-to-date standard tax rates (for merchants in the US, EU, Canada, Australia, and New Zealand)'
        ),
        disabled: false,
        availabilities: availablePlans.map(({ id, config }) => ({
          id,
          contentType: config[ecommerceFeatures.TAX_CALCULATIONS] ? 'icon-check' : 'icon-minus',
          compact: config[ecommerceFeatures.TAX_CALCULATIONS]
            ? t('Automatic tax calculations')
            : null,
        })),
      };
    },
  },
  [ecommerceFeatures.MARKETING_EMAILS]: {
    config(availablePlans) {
      return {
        id: ecommerceFeatures.MARKETING_EMAILS,
        title: t('Automated email marketing'),
        tooltip: t(
          "Send automated emails triggered by customers' actions, including favorite item reminders, feedback requests, and re-engagement emails"
        ),
        disabled: false,
        availabilities: availablePlans.map(({ id, config }) => ({
          id,
          contentType: config[ecommerceFeatures.MARKETING_EMAILS] ? 'icon-check' : 'icon-minus',
          compact: config[ecommerceFeatures.MARKETING_EMAILS]
            ? t('Automated email marketing')
            : null,
        })),
      };
    },
  },
  [ecommerceFeatures.PRE_ORDERS]: {
    config(availablePlans) {
      return {
        id: ecommerceFeatures.PRE_ORDERS,
        title: t('Pre-orders'),
        tooltip: t('Pre-orders let your customers order products that are currently out of stock.'),
        disabled: false,
        availabilities: availablePlans.map(({ id, config }) => ({
          id,
          contentType: config[ecommerceFeatures.PRE_ORDERS] ? 'icon-check' : 'icon-minus',
          compact: config[ecommerceFeatures.PRE_ORDERS] ? t('Pre-orders') : null,
        })),
      };
    },
  },
  [ecommerceFeatures.SUBSCRIPTIONS]: {
    config(availablePlans) {
      return {
        id: ecommerceFeatures.SUBSCRIPTIONS,
        title: t('Subscriptions'),
        tooltip: t(
          'Sell recurring subscriptions and charge your customers on a repeated schedule for products and services.'
        ),
        disabled: false,
        availabilities: availablePlans.map(({ id, config }) => ({
          id,
          contentType: config[ecommerceFeatures.SUBSCRIPTIONS] ? 'icon-check' : 'icon-minus',
          compact: config[ecommerceFeatures.SUBSCRIPTIONS] ? t('Subscriptions') : null,
        })),
      };
    },
  },
  [ecommerceFeatures.GIFT_CARDS]: {
    config(availablePlans) {
      return {
        id: ecommerceFeatures.GIFT_CARDS,
        title: t('Gift cards'),
        tooltip: t(
          "Enable customers to purchase digital gift cards for their friends and family (or their own future use), and they'll automatically receive an email with their unique redemption code"
        ),
        disabled: false,
        availabilities: availablePlans.map(({ id, config }) => ({
          id,
          contentType: config[ecommerceFeatures.GIFT_CARDS] ? 'icon-check' : 'icon-minus',
          compact: config[ecommerceFeatures.GIFT_CARDS] ? t('Gift cards') : null,
        })),
      };
    },
  },
  [ecommerceFeatures.COUPONS]: {
    config(availablePlans) {
      return {
        id: ecommerceFeatures.COUPONS,
        title: t('Discount coupons'),
        tooltip: t(
          'Build customer loyalty and increase sales by offering discount codes that customers enter at checkout'
        ),
        disabled: false,
        availabilities: availablePlans.map(({ id, config }) => ({
          id,
          contentType: config[ecommerceFeatures.COUPONS] ? 'icon-check' : 'icon-minus',
          compact: config[ecommerceFeatures.COUPONS] ? t('Discount coupons') : null,
        })),
      };
    },
  },
};

export default ecommerceFeatureConfiguration;
